import React, { useEffect, useRef, useState } from "react"

export default function Paging({ OnPageChange, pagingData, text = "Entries", children, loading = false }) {
    // jo page request kiya par server ne confirm nahi kiya
    const [pending, setPending] = useState(null)
    // double-fire rokne ke liye synchronous guard (re-render ka wait nahi karta)
    const lockRef = useRef(false)
    const wasLoading = useRef(loading)

    // server naya pageNumber de de -> dobara sync
    useEffect(() => {
        setPending(null)
        lockRef.current = false
    }, [pagingData?.pageNumber])

    // safety: loading true->false hote hi lock chhodo (agar server ne same page lauta diya)
    useEffect(() => {
        if (wasLoading.current && !loading) {
            setPending(null)
            lockRef.current = false
        }
        wasLoading.current = loading
    }, [loading])

    if (!pagingData) {
        return <div className="overflow-hidden rounded-xl border border-gray-200">{children}</div>
    }

    const pageCount = Number(pagingData.pageCount) || 0
    const serverPage = Number(pagingData.pageNumber) || 0
    const current = pending ?? serverPage              // UI hamesha intended page dikhaye
    const isFirst = current <= 1 || pageCount === 0
    const isLast = current >= pageCount || pageCount === 0
    const busy = loading || pending !== null
    const pendingDir = pending == null ? null : pending > serverPage ? "next" : "prev"

    const go = (target) => {
        if (lockRef.current || loading) return        // ek request chal rahi hai -> ignore (yahi desync rokta hai)
        if (target < 1 || target > pageCount) return
        lockRef.current = true                        // turant lock
        setPending(target)                            // UI turant update
        OnPageChange(target)                          // parent ko sirf number bhejo
    }

    const Spinner = () => (
        <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
        </svg>
    )

    const baseBtn =
        "inline-flex h-9 items-center gap-1.5 px-3.5 text-sm font-medium text-white transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 disabled:opacity-70"

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            {children}

            <div className="w-full border-t border-gray-100 bg-gray-50/80">
                <div className="flex flex-col items-center gap-3 p-3 sm:flex-row sm:justify-between">

                    {/* summary */}
                    <span aria-live="polite" className="text-center text-xs text-gray-500 sm:text-left sm:text-sm">
                        Showing{" "}
                        <span className="font-semibold text-gray-700">{pagingData.firstItemOnPage}</span>
                        –<span className="font-semibold text-gray-700">{pagingData.lastItemOnPage}</span> of{" "}
                        <span className="font-semibold text-gray-700">{pagingData.totalItemCount}</span> {text}
                        <span className="ml-1.5 inline-flex items-center whitespace-nowrap rounded-full bg-gray-200/70 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                            Page {current}/{pageCount}
                        </span>
                    </span>

                    {/* controls */}
                    <div className="inline-flex shrink-0 overflow-hidden rounded-lg shadow-sm">
                        <button
                            type="button"
                            aria-label="Previous page"
                            onClick={() => go(current - 1)}
                            disabled={busy || isFirst}
                            className={`${baseBtn} rounded-s-lg ${isFirst ? "cursor-not-allowed bg-brand-300" : "bg-brand-400 hover:bg-brand-500 active:scale-95"}`}
                        >
                            {busy && pendingDir === "prev" ? <Spinner /> : (
                                <svg className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5H1m0 0 4 4M1 5l4-4" />
                                </svg>
                            )}
                            Prev
                        </button>

                        <button
                            type="button"
                            aria-label="Next page"
                            onClick={() => go(current + 1)}
                            disabled={busy || isLast}
                            className={`${baseBtn} rounded-e-lg border-s border-brand-600 ${isLast ? "cursor-not-allowed bg-brand-300" : "bg-brand-400 hover:bg-brand-500 active:scale-95"}`}
                        >
                            Next
                            {busy && pendingDir === "next" ? <Spinner /> : (
                                <svg className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 5h12m0 0L9 1m4 4L9 9" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}